import { Chip, Grid } from '@mui/material';
import { parsePhoneNumber, parseSecondsToHM, parseSecondsToString } from '@nx-smartmonkey/shared/helpers';
import { TimeWindow } from '@nx-smartmonkey/shared/interfaces';
import { RoutalPalette } from '@nx-smartmonkey/ui';
import { CSSProperties, useRef } from 'react';
// import { useLongPress } from 'react-use';

import { DriversProject } from '../../../../../../domain/driversProject/DriversProject';
import { ProjectDriversFieldTypes, RouteStopFieldProps } from '../../../../../../domain/stops/Stop';
import { intl } from '../../../../../../helpers/IntGlobalProvider';
import { Text } from '../../../../../../react-components/Text';
import { StopDetailsImageByType } from './StopDetailsImageByType';

// FOr future versions. It seems to work bad on safari?

// const handleCopyLink = (data: string, showSnackbarMessage: (InfoErrorPayload: any) => void) => {
//   const el = document.createElement(`textarea`);
//   el.value = data;
//   const helper = document.getElementById(`helper_copy_assign`);
//   if (!helper) return;

//   helper.appendChild(el);
//   el.select();
//   document.execCommand(`copy`);
//   helper.removeChild(el);
//   showSnackbarMessage({ messageIntlId: `driver.stop.entry.copied`, type: `info`, autoHideDuration: 1000 });
// };

interface StopDetailsFieldProps {
  stopField: RouteStopFieldProps;
  project: DriversProject;
}

export const StopDetailsField = ({ stopField, project }: StopDetailsFieldProps) => {
  const { id, label, type, value } = stopField;

  const stringValueRef = useRef(``);

  //REMOVED FOR FUTURE VERSIONS. There was a problem with the scrollable and long click

  // const { showSnackbarMessage } = useShowSnackbarMessage();
  // const onLongPress = () => {
  //   handleCopyLink(stringValueRef.current, showSnackbarMessage);
  // };

  // const longPressEvent = useLongPress(onLongPress);

  if (!project) {
    return null;
  }

  const projectCustomFields = project.fields.stop_fields.filter((stopField) => stopField.is_custom_field);

  const renderField = ({
    id,
    type,
    value,
    project,
  }: {
    id: string;
    type: ProjectDriversFieldTypes;
    value: any;
    project: DriversProject;
  }): JSX.Element | null => {
    const customContentStyle: CSSProperties = { fontSize: `14px`, fontWeight: `normal`, userSelect: `text` };

    if (value === null || value === undefined) {
      return <Text variant="label" intlId="driver.stop.entry.empty" style={customContentStyle} />;
    }
    switch (
      id //Special label cases with units
    ) {
      case `weight`:
        stringValueRef.current = `${value} ${project.units.weight}`;

        return <Text variant="body" style={customContentStyle}>{`${value} ${project.units.weight}`}</Text>;

      case `volume`:
        stringValueRef.current = `${value} ${project.units.volume}`;
        return <Text variant="body" style={customContentStyle}>{`${value} ${project.units.volume}`}</Text>;

      case `requires`:
        return (
          <Grid item style={{ paddingTop: `2px`, paddingBottom: `2px` }}>
            {(value ?? []).map((requirement: string) => (
              <Chip
                style={{
                  margin: `2px`,
                  borderRadius: `5px`,
                  backgroundColor: RoutalPalette.secondary40,
                  padding: `0px 3px`,
                }}
                key={requirement}
                label={
                  <Text style={{ color: RoutalPalette.white, ...customContentStyle }} variant="body">
                    {requirement}
                  </Text>
                }
              />
            ))}
          </Grid>
        );
      case `url`:
        stringValueRef.current = value;
        return (
          <Text
            variant="body"
            style={{
              ...customContentStyle,
              color: RoutalPalette.primary20,
              textDecoration: `underline`,
              cursor: `pointer`,
            }}
            onClick={() => {
              const url = value.startsWith(`http`) ? value : `https://${value}`;
              window.open(url, `_blank`);
            }}
          >
            {value}
          </Text>
        );
      default:
        break;
    }

    if (type === `phone`) {
      //Special case phone due parsed

      const parsedPhoneNumber = parsePhoneNumber(value);
      if (parsedPhoneNumber) {
        stringValueRef.current = `+${parsedPhoneNumber.countryCallingCode} ${parsedPhoneNumber.nationalNumber}`;
        return (
          <Text
            variant="body"
            style={customContentStyle}
          >{`+${parsedPhoneNumber.countryCallingCode} ${parsedPhoneNumber.nationalNumber}`}</Text>
        );
      }
    }
    switch (
      type //Normal case
    ) {
      case `string`:
      case `number`:
      case `email`:
        stringValueRef.current = value;
        return (
          <Text variant="body" style={customContentStyle}>
            {value}
          </Text>
        );
      case `select`: {
        const customFieldDefinition = projectCustomFields.find((customField) => customField.id === stopField.id);

        if (!customFieldDefinition || !customFieldDefinition.options)
          return <Text variant="label" intlId="driver.stop.entry.empty" style={customContentStyle} />;

        stringValueRef.current = customFieldDefinition.options[value];
        return (
          <Text variant="body" style={customContentStyle}>
            {customFieldDefinition.options[value]}
          </Text>
        );
      }
      case `time`: //
        stringValueRef.current = parseSecondsToString(value);
        return (
          <Text variant="body" style={customContentStyle}>
            {parseSecondsToString(value)}
          </Text>
        );
      case `time_window`:
        return (
          <Grid item style={{ paddingTop: `2px`, paddingBottom: `2px` }}>
            {(value ?? []).map((timeWindow: TimeWindow) => (
              <Chip
                style={{
                  margin: `2px`,
                  borderRadius: `5px`,
                  backgroundColor: RoutalPalette.secondary40,
                  padding: `0px 3px`,
                }}
                key={timeWindow[0] + timeWindow[1]}
                label={
                  <Text
                    style={{ color: RoutalPalette.white, ...customContentStyle }}
                    variant="body"
                  >{`${parseSecondsToHM(timeWindow[0])}h - ${parseSecondsToHM(timeWindow[1])}h`}</Text>
                }
              />
            ))}
          </Grid>
        );

      case `multiselect`: {
        const customFieldDefinition = projectCustomFields.find((customField) => customField.id === stopField.id);

        if (!customFieldDefinition || !customFieldDefinition.options)
          return <Text variant="label" intlId="driver.stop.entry.empty" style={customContentStyle} />;

        return (
          <Grid item style={{ paddingTop: `2px`, paddingBottom: `2px` }}>
            {(value ?? []).map((selection: number, index: number) => (
              <Chip
                key={index}
                style={{
                  margin: `2px`,
                  borderRadius: `5px`,
                  backgroundColor: RoutalPalette.secondary40,
                  padding: `0px 3px`,
                }}
                label={
                  <Text style={{ color: RoutalPalette.white, ...customContentStyle }} variant="body">
                    {customFieldDefinition.options![selection]}
                  </Text>
                }
              />
            ))}
          </Grid>
        );
      }
      case `boolean`:
        if (value) {
          return <Text variant="body" style={customContentStyle} intlId="driver.stop.entry.boolean.yes" />;
        }
        return <Text variant="body" style={customContentStyle} intlId="driver.stop.entry.boolean.no" />;

      default:
        return null;
    }
  };

  return (
    <Grid
      key={id}
      style={{
        display: `flex`,
        flexDirection: `column`,
        minHeight: `48px`,
        margin: `6px 20px`,
        borderBottom: `1px solid ${RoutalPalette.grey9}`,
        overflow: `hidden`,
      }}
      // {...longPressEvent}
      onClick={() => {}}
    >
      <div id="helper_copy_assign" />
      <Grid>
        <Grid //Imagen + Titulo
          item
          style={{
            display: `flex`,
            flexDirection: `row`,
            alignItems: `center`,
            width: `100%`,
            height: `1.3em`,
          }}
        >
          <Grid>
            <StopDetailsImageByType typeItem={type} id={id} />
          </Grid>

          <Text style={{ fontSize: `12px`, fontWeight: `lighter` }} variant="label">
            {label ?? intl.formatMessage({ id: `driver.stop.entry.${id}` })}
          </Text>
        </Grid>
        <Grid item style={{ marginTop: `2px`, fontWeight: `lighter` }}>
          {renderField({
            id,
            type,
            value,
            project,
          })}
        </Grid>
      </Grid>
    </Grid>
  );
};
